import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { TenantAuthClaims } from '@models';
import { Observable, catchError, first, from, of, switchMap } from 'rxjs';
import { TenantContextService } from '@services';

/**
 * Restricts the club-manager portal (/dashboard/club) to MANAGER role only.
 *
 * ADMIN and REFEREE_COORDINATOR users navigating to /dashboard/club are
 * redirected to /dashboard/home (they have their own federation portal).
 */
export function managerOnlyGuard(): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const auth = inject(Auth);
    const router = inject(Router);
    const tenantContext = inject(TenantContextService);

    return from(auth.authStateReady()).pipe(
      switchMap(() => authState(auth).pipe(first())),
      catchError(() => of(null)),
      switchMap((user) => {
        if (!user) return of(router.createUrlTree(['/login']));

        return from(user.getIdTokenResult()).pipe(
          switchMap((tokenResult) => {
            const claims = tokenResult.claims as unknown as TenantAuthClaims;

            // Super-admins should not access the club portal (they have /platform)
            if (claims?.superAdmin === true) {
              return of(router.createUrlTree(['/dashboard/home']));
            }

            // Resolve role from the active tenant (respects switcher state)
            let activeTenantId: string | null = null;
            try {
              activeTenantId = tenantContext.tenantId;
            } catch {
              activeTenantId = claims?.tenants
                ? (Object.keys(claims.tenants)[0] ?? null)
                : null;
            }

            const userRole =
              activeTenantId && claims?.tenants?.[activeTenantId]
                ? claims.tenants[activeTenantId]
                : undefined;

            if (userRole === 'MANAGER') return of(true);

            // ADMIN, REFEREE_COORDINATOR, etc. → back to federation dashboard
            return of(router.createUrlTree(['/dashboard/home']));
          }),
          catchError(() => of(router.createUrlTree(['/login']))),
        );
      }),
    );
  };
}
